import React from 'react';
import { useEffect, useState, useMemo } from 'react';
import { Outlet, Route, Routes, useNavigate, useParams } from 'react-router';
import * as Sentry from '@sentry/react';
import { observer } from 'mobx-react-lite';
import { InfoCircledIcon } from '@radix-ui/react-icons';

import { ProjectRootScreen } from './pages/project-root.screen';
import { PageUnauthorized } from '../../components/unauthorized-placeholder/unauthorized-placeholder';
import { VideoComparePage } from './video-compare.page';
import { tabPool } from '../../features/tabs/models/tabs-pool';
import { uiStore } from '../../core/stores/ui-store';
import { CrashErrorScreen } from './components/crash-error-screen';
import { ProjectSettingsRoute } from './project-settings.route';
import { ProjectCalendarRoute } from './project-calendar.route';
import { useCurrentProject } from '../../hooks/use-current-project';
import { ProjectMenu } from './components/project-menu';
import { bootstrapProject, unarchiveProject } from '../../features/projects/store/projects.slice';
import { PageLoader } from '../../app/components/page-loader';
import { Task, Member } from '../../app/entities';
import { useCurrentMember } from '../../hooks/use-current-member';
import { formatDate } from '../../lib/utils/DateHelper';
import { catchError } from '../../core/catch-error';
import { ElevatedButton } from '@producer/ui-kit';

import styles from './project.page.module.scss';
export const ProjectPage = observer(() => {
  const { projectId } = useParams() as { projectId: string };
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);

  const [isDrawerOpen, setIsDrawerOpen] = useState(true);

  const project = useCurrentProject();
  const currentMember = useCurrentMember();

  /**
   * TODO URGENT
   *
   * How do we handle unauthorized errors ?
   */

  useEffect(() => {
    uiStore.isDataFetching = true;

    bootstrapProject(projectId!).then(() => {
      setIsLoading(false);
      Task.fetchByProject(projectId).then(() => (uiStore.isDataFetching = false));
    });
  }, [projectId]);

  // Check if user can unarchive
  const canUnarchive = useMemo(() => {
    if (!currentMember || !project) return false;
    if (currentMember.role === 'guest') return false;
    if (['owner', 'admin'].includes(currentMember.role)) return true;
    return project.createdBy === currentMember.userId;
  }, [currentMember, project]);

  const handleUnarchive = async () => {
    if (project) {
      try {
        await unarchiveProject(project._id);
      } catch (e) {
        catchError(e);
      }
    }
  };

  const archivedByMember = useMemo(() => {
    if (!project?.archivedById) return null;
    return Member.getOne(project.archivedById);
  }, [project?.archivedById]);

  useEffect(() => {
    if (project) {
      uiStore.pageTitle = {
        label: project.name,
        href: `/space/${project.spaceId}/projects/${project._id}`,
      };

      uiStore.showBack = true;
      uiStore.goBack = () => navigate('../');
    }

    tabPool.updateTab({
      key: projectId,
      label: project?.name,
    });

    return () => {
      uiStore.pageTitle = undefined;
      uiStore.showBack = false;
      uiStore.goBack = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, projectId]);

  return (
    <Sentry.ErrorBoundary fallback={<CrashErrorScreen />}>
      <Container>
        <ProjectMenu isOpen={isDrawerOpen} setIsOpen={setIsDrawerOpen} />

        <Content data-expand={isDrawerOpen}>
          {isLoading ? (
            <PageLoader />
          ) : (
            <>
              {project?.archived && (
                <ArchivedBanner>
                  <BannerContent>
                    <InfoCircledIcon />
                    <span>
                      This project was archived on{' '}
                      {project.archivedAt &&
                        formatDate(project.archivedAt.toString(), 'MMM dd, yyyy')}{' '}
                      {archivedByMember && `by ${archivedByMember.fullName}`}
                    </span>
                  </BannerContent>
                  {canUnarchive && <ElevatedButton text="Unarchive" onClick={handleUnarchive} />}
                </ArchivedBanner>
              )}

              <Outlet />

              {project ? (
                <Routes>
                  <Route
                    path="/steps/:stepId/assets/:assetId/compare-versions"
                    element={<VideoComparePage />}
                  />
                  <Route path="/settings" element={<ProjectSettingsRoute project={project} />} />
                  <Route path="/calendar" element={<ProjectCalendarRoute project={project} />} />
                  <Route path="/" element={<ProjectRootScreen />} />
                </Routes>
              ) : (
                <PageUnauthorized />
              )}
            </>
          )}
        </Content>
      </Container>
    </Sentry.ErrorBoundary>
  );
});

const Container = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => (
  <div
    className={className ? `${styles['container']} ${className}` : styles['container']}
    {...props}
  />
);

const Content = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => (
  <div className={className ? `${styles['content']} ${className}` : styles['content']} {...props} />
);

export const Header = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => (
  <div className={className ? `${styles['header']} ${className}` : styles['header']} {...props} />
);

const ArchivedBanner = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => (
  <div
    className={className ? `${styles['archived-banner']} ${className}` : styles['archived-banner']}
    {...props}
  />
);

const BannerContent = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => (
  <div
    className={className ? `${styles['banner-content']} ${className}` : styles['banner-content']}
    {...props}
  />
);
